import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Switch,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Drawer,
  Divider,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllAppServiceApi,
  updateAppServiceApi,
  reorderAppServiceApi,
  deleteAppServiceApi,
} from "../../../redux/api/api";
import ServicePreviewCard from "./ServicePreviewCard";

const STATUS_COLORS = { ACTIVE: "success", COMING_SOON: "warning", INACTIVE: "default" };
const STATUS_LABEL = { ACTIVE: "Active", COMING_SOON: "Coming Soon", INACTIVE: "Inactive" };

const StatusChip = ({ status }) => (
  <Chip
    size="small"
    label={STATUS_LABEL[status] || status}
    color={STATUS_COLORS[status] || "default"}
    variant={status === "ACTIVE" ? "filled" : "outlined"}
  />
);

const pickLang = (block, lang = "en") => (block && (block[lang] || block.en)) || "";

const CustomerAppServiceList = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuService, setMenuService] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await getAllAppServiceApi();
      setServices(response?.data?.info || []);
    } catch (error) {
      toast.error(
        error?.response?.status === 402 || error?.response?.status === 401
          ? "Your session has expired — please log in again."
          : error?.response?.data?.message || "Unable to load customer app services.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openMenu = (event, service) => {
    setMenuAnchor(event.currentTarget);
    setMenuService(service);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuService(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const next = Array.from(services);
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    const reordered = next.map((s, index) => ({ ...s, displayOrder: index + 1 }));
    setServices(reordered);
    try {
      const response = await reorderAppServiceApi(reordered.map((s) => ({ id: s._id, displayOrder: s.displayOrder })));
      if (response?.data?.info) setServices(response.data.info);
      toast.success("Order updated.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save the new order.");
      load();
    }
  };

  const patchService = async (service, patch, successMsg) => {
    try {
      await updateAppServiceApi({ id: service._id, ...patch });
      toast.success(successMsg);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed.");
    }
  };

  const toggleHomeVisibility = (service) =>
    patchService(
      service,
      { visibility: { ...service.visibility, showOnHome: !service.visibility?.showOnHome } },
      "Home visibility updated.",
    );

  const setStatus = (service, status) => {
    closeMenu();
    patchService(service, { status }, `Status set to ${STATUS_LABEL[status]}.`);
  };

  const handleDelete = async (service) => {
    closeMenu();
    if (!window.confirm(`Delete "${pickLang(service.displayName) || service.internalName}"? This cannot be undone.`)) return;
    try {
      await deleteAppServiceApi(service._id);
      toast.success("Service deleted.");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete service.");
    }
  };

  const homeServices = services
    .filter((s) => s.status === "ACTIVE" && s.visibility?.showOnHome)
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>Customer App Services</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>App Configuration</li>
          <li>Customer App Services</li>
        </ul>
      </div>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Services {services.length ? `(${services.length})` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Drag rows to set the order tiles appear in on the customer app Home screen.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => setPreviewOpen(true)}>
                Customer Preview
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/app-config/customer-app-services/create")}>
                Add Service
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell sx={{ width: 70 }} align="center">
                    Order
                  </TableCell>
                  <TableCell sx={{ width: 64 }}>Icon</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Internal Service</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Mapped Service</TableCell>
                  <TableCell align="center">Home</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="app-services">
                  {(dropProvided) => (
                    <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            Loading services…
                          </TableCell>
                        </TableRow>
                      ) : services.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                            No services configured yet. Click “Add Service” to publish the first tile.
                          </TableCell>
                        </TableRow>
                      ) : (
                        services.map((service, index) => (
                          <Draggable key={service._id} draggableId={service._id} index={index}>
                            {(dragProvided, snapshot) => (
                              <TableRow
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                hover
                                sx={{
                                  background: snapshot.isDragging ? "#f5f7ff" : "inherit",
                                  ...dragProvided.draggableProps.style,
                                }}
                              >
                                <TableCell {...dragProvided.dragHandleProps} sx={{ cursor: "grab", color: "text.disabled" }}>
                                  <DragIndicatorIcon fontSize="small" />
                                </TableCell>
                                <TableCell align="center">{service.displayOrder}</TableCell>
                                <TableCell>
                                  <Avatar
                                    variant="rounded"
                                    src={service.icon?.type === "IMAGE" ? service.icon?.imageUrl : undefined}
                                    sx={{ width: 36, height: 36, bgcolor: service.card?.backgroundColor || "#f0f0f4" }}
                                  >
                                    <ImageIcon fontSize="small" color="disabled" />
                                  </Avatar>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, cursor: "pointer" }}
                                    onClick={() => navigate(`/app-config/customer-app-services/${service._id}/edit`)}
                                  >
                                    {pickLang(service.displayName) || "—"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {pickLang(service.subtitle)}
                                  </Typography>
                                </TableCell>
                                <TableCell>{service.internalName}</TableCell>
                                <TableCell>{service.categoryId?.name || "—"}</TableCell>
                                <TableCell>
                                  <Chip size="small" variant="outlined" label={service.businessServiceId || service.serviceId} />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title={service.visibility?.showOnHome ? "Shown on Home" : "Hidden from Home"}>
                                    <Switch
                                      size="small"
                                      checked={Boolean(service.visibility?.showOnHome)}
                                      onChange={() => toggleHomeVisibility(service)}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <StatusChip status={service.status} />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={(e) => openMenu(e, service)}>
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))
                      )}
                      {dropProvided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            navigate(`/app-config/customer-app-services/${menuService?._id}/edit`);
            closeMenu();
          }}
        >
          Edit
        </MenuItem>
        {menuService?.status !== "ACTIVE" ? <MenuItem onClick={() => setStatus(menuService, "ACTIVE")}>Set Active</MenuItem> : null}
        {menuService?.status !== "COMING_SOON" ? (
          <MenuItem onClick={() => setStatus(menuService, "COMING_SOON")}>Set Coming Soon</MenuItem>
        ) : null}
        {menuService?.status !== "INACTIVE" ? <MenuItem onClick={() => setStatus(menuService, "INACTIVE")}>Set Inactive</MenuItem> : null}
        <MenuItem sx={{ color: "error.main" }} onClick={() => handleDelete(menuService)}>
          Delete
        </MenuItem>
      </Menu>

      <Drawer anchor="right" open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box sx={{ width: 340, p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Customer App Preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top Services — active + Home-visible, in display order. Approximate; the app owns the real layout.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {homeServices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active, Home-visible services yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {homeServices.map((s) => (
                <ServicePreviewCard
                  key={s._id}
                  service={{
                    displayName: pickLang(s.displayName) || s.internalName,
                    subtitle: pickLang(s.subtitle),
                    icon: s.icon,
                    backgroundColor: s.card?.backgroundColor || null,
                    status: s.status,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default CustomerAppServiceList;
